import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { Modal } from 'antd';
import classNames from 'classnames';
import './instructionModal.less';

const TAB_LIST = [
    { key: 'what', label: '是什么' },
    { key: 'how', label: '怎么用' },
    { key: 'status', label: '看状态' },
] as const;

type TabKey = (typeof TAB_LIST)[number]['key'];

const SECTION_CONTENT: Record<TabKey, { title: string; desc?: string; img: string; imgHeight: number }> = {
    what: {
        title: '1.总预测分与考点预测分',
        img: 'https://res.gaodunwangxiao.com/tools/2025-07-02/37100714_what.png',
        imgHeight: 347,
    },
    how: {
        title: '2.考点练习和考点详情',
        img: 'https://res.gaodunwangxiao.com/tools/2025-07-02/37107197_how.png',
        imgHeight: 347,
    },
    status: {
        title: '3.考点任务的状态',
        desc: '达到考纲对该考点的预测分要求，即为考点达标。',
        img: 'https://res.gaodunwangxiao.com/tools/2025-07-02/37104443_status.png',
        imgHeight: 327,
    },
};

interface InstructionModalProps {
    open: boolean;
    onCancel: () => void;
}

const InstructionModal: React.FC<InstructionModalProps> = ({ open, onCancel }) => {
    const [activeTab, setActiveTab] = useState<TabKey>('what');
    const [lockTab, setLockTab] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const sectionRefs = useMemo((): Record<TabKey, React.RefObject<HTMLDivElement>> => {
        const refs: Record<TabKey, React.RefObject<HTMLDivElement>> = {
            what: React.createRef<HTMLDivElement>(),
            how: React.createRef<HTMLDivElement>(),
            status: React.createRef<HTMLDivElement>(),
        };
        return refs;
    }, []);
    const lockTimer = useRef<NodeJS.Timeout | null>(null);

    // Tab点击
    const handleTabClick = useCallback(
        (key: TabKey) => {
            setActiveTab(key);
            setLockTab(true);
            sectionRefs[key].current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
            if (lockTimer.current) clearTimeout(lockTimer.current);
            lockTimer.current = setTimeout(() => setLockTab(false), 400);
        },
        [sectionRefs]
    );

    // 滚动联动
    const handleScroll = useCallback(() => {
        if (lockTab) return;
        const contentEl = contentRef.current;
        if (!contentEl) return;
        const center = contentEl.scrollTop + contentEl.clientHeight / 2;
        for (const { key } of TAB_LIST) {
            const el = sectionRefs[key].current;
            if (!el) continue;
            const top = el.offsetTop;
            const bottom = top + el.offsetHeight;
            if (center >= top && center < bottom) {
                setActiveTab(key);
                break;
            }
        }
    }, [lockTab, sectionRefs]);

    // 清理定时器
    useEffect(() => {
        return () => {
            if (lockTimer.current) clearTimeout(lockTimer.current);
        };
    }, []);

    useEffect(() => {
        if (!open && contentRef.current) {
            contentRef.current.scroll({ top: 0 });
        }
    }, [open]);

    return (
        <Modal
            title={<div className="sub-tiku__instruction-modal-title">考点任务说明</div>}
            open={open}
            centered
            onCancel={onCancel}
            width={560}
            footer={null}
            styles={{
                content: { padding: 24 },
                header: { marginBottom: 20 },
                body: { padding: 0 },
            }}
        >
            <div>
                {/* Tab 切换 */}
                <div className="sub-tiku__instruction-modal-tab-bar">
                    {TAB_LIST.map(tab => (
                        <div
                            key={tab.key}
                            onClick={() => handleTabClick(tab.key)}
                            className={classNames('sub-tiku__instruction-modal-tab-btn', activeTab === tab.key ? 'active' : '')}
                        >
                            {tab.label}
                        </div>
                    ))}
                </div>
                {/* 内容区 */}
                <div
                    ref={contentRef}
                    onScroll={handleScroll}
                    className="sub-tiku__instruction-modal-content sub-tiku__instruction-modal-custom-bar"
                >
                    {TAB_LIST.map((tab, idx) => (
                        <div
                            key={tab.key}
                            ref={sectionRefs[tab.key]}
                            className={classNames('sub-tiku__instruction-modal-section', { 'mt-10': idx !== 0 })}
                        >
                            <div className="sub-tiku__instruction-modal-section-title">
                                <h3 className="sub-tiku__instruction-modal-section-h3">{SECTION_CONTENT[tab.key].title}</h3>
                                {SECTION_CONTENT[tab.key].desc && (
                                    <p className="sub-tiku__instruction-modal-section-desc">{SECTION_CONTENT[tab.key].desc}</p>
                                )}
                            </div>
                            <img
                                src={SECTION_CONTENT[tab.key].img}
                                alt={tab.label}
                                className="sub-tiku__instruction-modal-img"
                                style={{ width: 512, height: SECTION_CONTENT[tab.key].imgHeight }}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    );
};

export default InstructionModal;
